<template>
  <v-card
    :loading="loading"
    class="textCard mb-2"
    :flat="normalizedOptions.flat"
  >
    <v-card-title v-if="normalizedOptions.title">{{ title }}</v-card-title>
    <v-card-text fluid>
      <v-form ref="form" v-model="valid">
        <template v-if="normalizedOptions.tabs">
          <field-list
            :modules="modules"
            :structure="structure"
            :fields-source="allLangFields"
            langs="all"
          />
        </template>
        <v-tabs
          v-if="normalizedOptions.tabs"
          v-model="tab"
          background-color="cyan"
          color="yellow"
          dark
          slider-color="yellow"
          slider-size="3"
          center-active
          fixed-tabs
        >
          <v-tab v-for="lang in normalizedOptions.tabLangs" :key="lang">
            {{ lang }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab" v-if="normalizedOptions.tabs">
          <v-tab-item v-for="lang in normalizedOptions.tabLangs" :key="lang">
            <v-card-text>
              <field-list
                :modules="modules"
                :structure="structure"
                :fields-source="langGrouped[lang]"
                :langs="lang"
                un-lang-prepend
              />
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
        <template v-else>
          <field-list
            :modules="modules"
            :structure="structure"
            :fields-source="modulesFieldsSource"
          />
        </template>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn text color="primary" :disabled="loading" @click="save">
        Сохранить
      </v-btn>
      <v-btn
        text
        color="deep-purple lighten-3"
        :disabled="loading"
        @click="load"
      >
        Перезагрузить
      </v-btn>
    </v-card-actions>
    <snackbar v-bind.sync="snackbar" />
  </v-card>
</template>

<script>
import transport from "../../mixins/transport";
import snackbarError from "../../mixins/snackbarError";
import Snackbar from "../Snackbar";
import FieldList from "./FieldList";

const defaultOptions = {
  tabs: false,
  tabLangs: "auto",
  flat: false,
  title: true
};

export default {
  name: "InputBlock",
  components: { FieldList, Snackbar },
  mixins: [transport, snackbarError],
  props: {
    modules: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      default: "Тексты"
    },
    options: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      valid: true,
      tab: null,
      langIcons: {
        ru: "🇷🇺",
        kk: "🇰🇿",
        en: "🇬🇧",
        all: "mdi-translate",
        any: "?"
      },
      langLabels: {
        ru: "рус.",
        kk: "каз.",
        en: "англ.",
        all: "все яз.",
        any: "любой яз."
      },
      structure: {}
    };
  },
  watch: {
    modules: {
      deep: true,
      immediate: true,
      handler: "fillStructure"
    }
  },
  computed: {
    modulesFieldsSource() {
      const result = {};
      Object.keys(this.modules).forEach((module) => {
        result[module] = Object.keys(this.modules[module]);
      });
      return result;
    },
    normalizedOptions() {
      const result =
        this.options === null
          ? Object.assign({}, defaultOptions)
          : Object.assign({}, defaultOptions, this.options);
      if (result.tabs && result.tabLangs === "auto")
        result.tabLangs = this.collectedLangs;
      return result;
    },

    collectedLangs() {
      const langs = [];
      Object.values(this.modules).forEach((fields) => {
        Object.values(fields).forEach((data) => {
          if (typeof data.langs === "undefined") return;
          data.langs.forEach((lang) => {
            if (lang === "all") return;
            if (langs.includes(lang)) return;
            langs.push(lang);
          });
        });
      });
      return langs;
    },

    allLangFields() {
      const result = {};
      let fieldData;
      for (let module in this.structure) {
        if (!this.structure.hasOwnProperty(module)) continue;
        for (let field in this.structure[module]) {
          if (!this.structure[module].hasOwnProperty(field)) continue;
          fieldData = this.structure[module][field];
          if (typeof fieldData.langs === "undefined") return;
          fieldData.langs.some((lang) => {
            if (lang !== "all") return false;
            if (typeof result[module] === "undefined") result[module] = [];
            result[module].push(field);
            return true;
          });
        }
      }
      return result;
    },

    langGrouped() {
      const result = {};
      let fieldData;
      this.normalizedOptions.tabLangs.forEach((lang) => {
        result[lang] = {};
      });
      for (let module in this.structure) {
        if (!this.structure.hasOwnProperty(module)) continue;
        for (let field in this.structure[module]) {
          if (!this.structure[module].hasOwnProperty(field)) continue;
          fieldData = this.structure[module][field];
          if (typeof fieldData.langs === "undefined") return;
          fieldData.langs.some((lang) => {
            if (lang === "all") {
              return true;
            }
            if (typeof result[lang][module] === "undefined")
              result[lang][module] = [];
            result[lang][module].push(field);
            return false;
          });
        }
      }
      return result;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    fillStructure() {
      const structure = {};
      for (const [module, fields] of Object.entries(this.modules)) {
        structure[module] = {};
        for (const [field, data] of Object.entries(fields)) {
          if (data.type === "divider") continue;
          structure[module][field] = {
            type: data.type,
            langs: data.langs,
            models: {}
          };
          if (data.type === "image") {
            structure[module][field].size = data.size;
          }
          data.langs.forEach((lang) => {
            structure[module][field].models[lang] = null;
          });
        }
      }
      this.structure = structure;
    },
    parseResponse(responseData) {
      for (const [module, fields] of Object.entries(responseData)) {
        for (const [field, langs] of Object.entries(fields)) {
          for (const [lang, value] of Object.entries(langs)) {
            this.$set(this.structure[module][field].models, lang, value);
          }
        }
      }
    },
    load() {
      if (this.loading) return;
      this.loading = true;
      this.transport(
        "POST",
        "utils/getTexts",
        { structure: this.structure },
        (response) => {
          this.parseResponse(response.data);
        },
        this.showSnack,
        () => {
          this.loading = false;
        }
      );
    },
    save() {
      if (this.loading) return;
      this.$refs.form.validate();
      if (!this.valid) return;
      this.loading = true;
      this.transport(
        "POST",
        "utils/setTexts",
        { structure: this.structure },
        (response) => {
          this.parseResponse(response.data);
          this.successSnack("сохранено");
          this.$emit("saved");
        },
        this.showSnack,
        () => {
          this.loading = false;
        }
      );
    }
  }
};
</script>

